import { useCallback, useContext, useMemo, useState } from 'react';
import { Card, ListGroup } from 'react-bootstrap';
import { ApprovalStages } from '@blisspointmedia/bpm-types/dist/CreativeApprovals';
import { Button } from '@Components/Button/Button';
import { Appearance, Design, Intent, Size } from '@Components/designConstants';
import { PieChart } from '@Components/PieChart';
import { useCreativeMap } from '../../redux/creative';
import { useSetAreYouSure } from '../../redux/modals';
import { useCompanyInfo } from '../../utils/hooks/useCompanyInfo';
import { StaticCard } from './Cards';
import { PieSetter } from './PieSetter';
import { StreamingCreativesContext, TabKey } from './streaming-creatives-constants';
import { makeNetPlatKey } from './streaming-creatives-utils';

export const Cart = () => {
  const { cid: company } = useCompanyInfo();
  const { creativeMap, colorMap } = useCreativeMap({
    company,
    mediaTypes: ['streaming', 'audio', 'display']
  });
  const {
    setSelectedMap,
    goToTab,
    cart,
    setWorkspaceMap,
    committing,
    commit,
    setCart,
    shopData,
    approvalStageData
  } = useContext(StreamingCreativesContext);

  const setAreYouSure = useSetAreYouSure();

  const [selectedId, setSelectedId] = useState<number | undefined>(cart[0]?.id);
  const [expandedNetwork, setExpandedNetwork] = useState<string>();

  const [selectedItem, items] = useMemo(() => {
    const selected = cart.find(item => item.id === selectedId);
    const rows = cart.map(item => ({
      id: item.id,
      placements: item.placements,
      rotation: item.rotation,
      label: [
        ...new Set(item.placements.map(id => shopData.placementMap[id]?.network).filter(Boolean))
      ]
        .sort()
        .join(', ')
    }));
    return [selected, rows] as const;
  }, [cart, selectedId, shopData]);

  const [networkGroups, subPlacements] = useMemo(() => {
    const groups: Record<string, { key: string; network: string; platform: string }> = {};
    const subs: string[] = [];

    for (const id of selectedItem?.placements || []) {
      const placement = shopData.placementMap[id];
      if (!placement) {
        continue;
      }
      const key = makeNetPlatKey(placement);
      if (key === expandedNetwork) {
        subs.push(placement.name);
      }
      groups[key] = { key, network: placement.network, platform: placement.platform };
    }

    return [Object.values(groups).sort((a, b) => a.key.localeCompare(b.key)), subs.sort()] as const;
  }, [selectedItem, expandedNetwork, shopData]);

  const removeFromCart = useCallback(() => {
    setCart(cart.filter(item => item.id !== selectedId));
  }, [selectedId, cart, setCart]);

  const handleAreYouSureModal = useCallback(() => {
    if (approvalStageData?.stage === ApprovalStages.APPROVED) {
      setAreYouSure({
        title: 'Committing New Changes Before Uploading Approved Changes.',
        message:
          'There are creative allocation changes that have been approved, but not yet uploaded. If you commit new changes, this approval will be canceled. Continue anyway?',
        okayText: 'Yes, Commit',
        cancelText: "No, Don't Commit",
        onOkay: commit
      });
      return;
    }
    if (approvalStageData?.stage === ApprovalStages.IN_REVIEW) {
      setAreYouSure({
        title: 'Committing New Changes Before Finishing Current Approvals Process.',
        message:
          'There are already creative allocation changes in review. If you commit new changes, the current approval process will be canceled. Continue anyway?',
        okayText: 'Yes, Commit',
        cancelText: "No, Don't Commit",
        onOkay: commit
      });
      return;
    }

    commit();
  }, [approvalStageData, setAreYouSure, commit]);

  return (
    <div className='streaming-creatives-card-container'>
      <Card className='streaming-creatives-card'>
        <div className='cart-list'>
          {cart.length ? (
            <>
              <div className='item-list'>
                <ListGroup>
                  {items.map(({ id, rotation, label }) => (
                    <ListGroup.Item
                      key={id}
                      active={selectedId === id}
                      onClick={() => setSelectedId(id)}
                    >
                      <div className='main-item'>
                        <PieChart
                          data={rotation.map(({ isci, value }) => ({
                            name: isci,
                            value,
                            color: colorMap[isci] || 'white'
                          }))}
                          size={40}
                        />
                        <div className='item-label'>{label}</div>
                      </div>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              </div>
              <div className='preview'>
                <div className='actions'>
                  <Button
                    size={Size.LG}
                    appearance={Appearance.OUTLINED}
                    onClick={() => {
                      if (!selectedItem) {
                        return;
                      }
                      const newSelectedMap: Record<string, boolean> = {};
                      for (const id of selectedItem.placements) {
                        newSelectedMap[id] = true;
                      }
                      const newWorkspaceMap: Record<string, number> = {};
                      for (const { isci, value } of selectedItem.rotation) {
                        newWorkspaceMap[isci] = value;
                      }
                      setSelectedMap(newSelectedMap);
                      setWorkspaceMap(newWorkspaceMap);
                      removeFromCart();
                      goToTab(TabKey.WORKSPACE);
                    }}
                  >
                    Edit
                  </Button>
                  <Button
                    size={Size.LG}
                    appearance={Appearance.OUTLINED}
                    design={Intent.ERROR}
                    onClick={() => {
                      if (items.length === 1) {
                        goToTab(TabKey.MENU);
                      }
                      setSelectedId(cart[0].id);
                      removeFromCart();
                    }}
                  >
                    Delete
                  </Button>
                </div>
                <div className='pie-box'>
                  <PieSetter
                    pieFlex={0.5}
                    data={(selectedItem?.rotation || []).map(({ isci, value }) => {
                      const { name, length } = creativeMap?.[isci] || {
                        name: 'unnamed',
                        length: 0
                      };
                      return {
                        isci,
                        name: isci,
                        key: isci,
                        color: colorMap[isci] || 'white',
                        value,
                        creative: name,
                        length,
                        isLive: true
                      };
                    })}
                    renderItem={elem => <StaticCard {...elem} />}
                  />
                </div>
                <div className='placement-list'>
                  <div className='network-groups'>
                    <ListGroup>
                      {networkGroups.map(({ key, network, platform }) => (
                        <ListGroup.Item
                          key={key}
                          className='placement-item'
                          active={expandedNetwork === key}
                          onClick={() => setExpandedNetwork(key)}
                        >
                          {network} {platform}
                        </ListGroup.Item>
                      ))}
                    </ListGroup>
                  </div>
                  <div className='sub-placements'>
                    <ListGroup>
                      {subPlacements.map(item => (
                        <ListGroup.Item key={item}>{item}</ListGroup.Item>
                      ))}
                    </ListGroup>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className='cart-empty'>Cart is Empty</div>
          )}
        </div>
        <div className='buttons-container'>
          <Button
            size={Size.LG}
            appearance={Appearance.OUTLINED}
            design={Design.NEUTRAL}
            onClick={() => goToTab(TabKey.WORKSPACE)}
          >
            Back
          </Button>
          {Boolean(cart.length) && (
            <Button
              size={Size.LG}
              loading={committing}
              disabled={!cart.length}
              onClick={handleAreYouSureModal}
            >
              Commit
            </Button>
          )}
        </div>
      </Card>
    </div>
  );
};
